import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createHolding, getBrokers } from "../../services/portfolioService";

// Market configuration
const MARKETS = [
  {
    id: "US",
    name: "United States",
    suffix: "",
    currency: "USD",
    exchange: "NASDAQ",
  },
  {
    id: "RO",
    name: "Romania",
    suffix: ".RO",
    currency: "RON",
    exchange: "BVB",
  },
  {
    id: "DE",
    name: "Germany",
    suffix: ".DE",
    currency: "EUR",
    exchange: "XETRA",
  },
  {
    id: "UK",
    name: "United Kingdom",
    suffix: ".L",
    currency: "GBP",
    exchange: "LSE",
  },
  {
    id: "FR",
    name: "France",
    suffix: ".PA",
    currency: "EUR",
    exchange: "EURONEXT",
  },
  {
    id: "NL",
    name: "Netherlands",
    suffix: ".AS",
    currency: "EUR",
    exchange: "EURONEXT",
  },
  {
    id: "CH",
    name: "Switzerland",
    suffix: ".SW",
    currency: "CHF",
    exchange: "SIX",
  },
  { id: "JP", name: "Japan", suffix: ".T", currency: "JPY", exchange: "TSE" },
  {
    id: "HK",
    name: "Hong Kong",
    suffix: ".HK",
    currency: "HKD",
    exchange: "HKEX",
  },
];

const CSV_TEMPLATE = `Broker,Market,Ticker,Company,Type,Quantity,Price,Date,Currency,Fees,Notes
XTB,US,AAPL,Apple Inc.,BUY,10,150.50,2024-01-15,USD,9.99,First purchase
eToro,RO,SNN,OMV Petrom,BUY,100,0.45,2024-01-20,RON,5.00,Romanian stock
Trading212,DE,BMW,BMW AG,BUY,5,95.30,2024-01-25,EUR,0,German stock
XTB,US,GOOGL,Alphabet Inc.,BUY,3,2850.00,2024-02-01,USD,9.99,Tech stock
eToro,RO,TLV,Banca Transilvania,BUY,50,25.50,2024-02-10,RON,5.00,Romanian bank`;

const PortfolioImportPage = () => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [importing, setImporting] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const navigate = useNavigate();

  React.useEffect(() => {
    fetchBrokers();
  }, []);

  const fetchBrokers = async () => {
    try {
      const data = await getBrokers();
      setBrokers(data);
    } catch (error) {
      console.error("Error fetching brokers:", error);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith(".csv")) {
        setError("Please upload a CSV file");
        return;
      }
      setFile(selectedFile);
      parseCSV(selectedFile);
    }
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.trim().split("\n");

        if (lines.length < 2) {
          setError("CSV file is empty or invalid");
          return;
        }

        const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

        // Validate required headers
        const requiredHeaders = [
          "broker",
          "market",
          "ticker",
          "type",
          "quantity",
          "price",
          "date",
        ];
        const missingHeaders = requiredHeaders.filter(
          (h) => !headers.includes(h)
        );
        if (missingHeaders.length > 0) {
          setError(`Missing required columns: ${missingHeaders.join(", ")}`);
          return;
        }

        const data = lines.slice(1).map((line, index) => {
          const values = line.split(",").map((v) => v.trim());
          const row = {};
          headers.forEach((header, i) => {
            row[header] = values[i] || "";
          });

          // Parse and convert date to YYYY-MM-DD format
          let formattedDate = row.date;
          if (row.date) {
            // Try DD-MM-YYYY format
            if (row.date.match(/^\d{2}-\d{2}-\d{4}$/)) {
              const [day, month, year] = row.date.split("-");
              formattedDate = `${year}-${month}-${day}`;
            }
            // Try DD/MM/YYYY format
            else if (row.date.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const [day, month, year] = row.date.split("/");
              formattedDate = `${year}-${month}-${day}`;
            }
            // Already in YYYY-MM-DD format or other, leave as is
          }

          // Find market from Market column
          const marketCode = row.market.toUpperCase();
          const market = MARKETS.find(
            (m) =>
              m.id === marketCode ||
              m.exchange === marketCode ||
              m.name.toUpperCase() === marketCode
          );

          if (!market && marketCode) {
            console.warn(
              `Unknown market: ${row.market} for ticker ${row.ticker}`
            );
          }

          const selectedMarket = market || MARKETS[0]; // Default to US

          return {
            ...row,
            date: formattedDate,
            lineNumber: index + 2,
            ticker: row.ticker.toUpperCase() + (selectedMarket.suffix || ""),
            market: selectedMarket.id,
            exchange: selectedMarket.exchange,
            currency: row.currency || selectedMarket.currency,
          };
        });

        setParsedData(data);
        setError(null);
      } catch (err) {
        console.error("Error parsing CSV:", err);
        setError("Failed to parse CSV file. Please check the format.");
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      setError("No data to import");
      return;
    }

    setImporting(true);
    setError(null);
    setSuccess(null);

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (const row of parsedData) {
      try {
        // Find or create broker
        let broker = brokers.find(
          (b) => b.name.toLowerCase() === row.broker.toLowerCase()
        );

        if (!broker) {
          setError(
            `Broker "${row.broker}" not found. Please create brokers first.`
          );
          setImporting(false);
          return;
        }

        // Validate required fields
        if (
          !row.ticker ||
          !row.type ||
          !row.quantity ||
          !row.price ||
          !row.date
        ) {
          errors.push(`Line ${row.lineNumber}: Missing required fields`);
          errorCount++;
          continue;
        }

        await createHolding({
          broker_id: broker.id,
          ticker_symbol: row.ticker,
          company_name: row.company || "",
          exchange: row.exchange,
          transaction_type: row.type.toUpperCase(),
          quantity: parseFloat(row.quantity),
          purchase_price: parseFloat(row.price),
          purchase_date: row.date,
          currency: row.currency,
          fees: parseFloat(row.fees || 0),
          notes: row.notes || "",
        });

        successCount++;
      } catch (error) {
        console.error(`Error importing line ${row.lineNumber}:`, error);
        errors.push(`Line ${row.lineNumber}: ${error.message}`);
        errorCount++;
      }
    }

    setImporting(false);

    if (successCount > 0) {
      setSuccess(`Successfully imported ${successCount} transaction(s)`);
    }

    if (errorCount > 0) {
      setError(
        `Failed to import ${errorCount} transaction(s):\n${errors.join("\n")}`
      );
    }

    if (successCount > 0 && errorCount === 0) {
      setTimeout(() => {
        navigate("/stock-transactions");
      }, 2000);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "portfolio-import-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          <UploadFileIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Import Portfolio
        </Typography>

        <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            <strong>CSV Format with Market Column:</strong>
          </Typography>
          <Typography variant="body2" component="div">
            Required columns: Broker, <strong>Market</strong>, Ticker, Type,
            Quantity, Price, Date
            <br />
            Optional: Company, Currency, Fees, Notes
            <br />
            <br />
            <strong>Market values:</strong> US, RO (Romania), DE (Germany), UK,
            FR (France), NL (Netherlands), CH (Switzerland), JP (Japan), HK
            (Hong Kong)
            <br />
            <strong>Date format:</strong> YYYY-MM-DD (e.g., 2024-01-15) or
            DD-MM-YYYY (e.g., 15-01-2024) - both work!
            <br />
            The ticker suffix (.RO, .DE, etc.) will be added automatically based
            on the Market column!
          </Typography>
        </Alert>

        <Box display="flex" gap={2} mb={3}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
          >
            Download Template
          </Button>
          <Button variant="contained" component="label">
            Choose CSV File
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleFileChange}
            />
          </Button>
        </Box>

        {file && (
          <Alert severity="success" sx={{ mb: 2 }}>
            File selected: {file.name}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {parsedData.length > 0 && (
          <>
            <Typography variant="h6" gutterBottom>
              Preview ({parsedData.length} transactions)
            </Typography>
            <Box sx={{ overflowX: "auto", mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Broker</TableCell>
                    <TableCell>Market</TableCell>
                    <TableCell>Ticker</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Currency</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedData.slice(0, 10).map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.broker}</TableCell>
                      <TableCell>{row.market}</TableCell>
                      <TableCell>{row.ticker}</TableCell>
                      <TableCell>{row.company || "-"}</TableCell>
                      <TableCell>{row.type}</TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                      <TableCell align="right">{row.price}</TableCell>
                      <TableCell>{row.date}</TableCell>
                      <TableCell>{row.currency}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {parsedData.length > 10 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Showing first 10 of {parsedData.length} transactions
                </Typography>
              )}
            </Box>

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={importing}
              >
                {importing ? (
                  <CircularProgress size={24} />
                ) : (
                  `Import ${parsedData.length} Transactions`
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFile(null);
                  setParsedData([]);
                  setError(null);
                }}
                disabled={importing}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default PortfolioImportPage;
